import { MessageSquareText, Send, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { submitUserFeedback } from '../api.js'
import { useLanguage } from '../contexts/LanguageContext.jsx'

const copy = {
  zh: {
    button: '反馈/建议',
    title: '反馈/建议',
    desc: '遇到问题、看不懂或有建议，都可以直接告诉我们。',
    type: '问题类型',
    content: '反馈内容',
    contact: '联系方式（选填）',
    contentPlaceholder: '请描述你遇到的问题、所在页面或希望改进的地方',
    contactPlaceholder: '微信 / 邮箱 / 手机号，选填',
    page: '当前页面',
    submit: '提交反馈',
    submitting: '提交中...',
    success: '已收到反馈，谢谢你帮我们把产品打磨得更稳。',
    error: '提交失败，请稍后重试，或通过客服入口联系我们。',
    required: '请先填写反馈内容。',
    close: '关闭反馈',
    types: [
      ['bug', 'Bug'],
      ['suggestion', '建议'],
      ['confused', '不知道怎么用'],
      ['payment', '付费问题'],
    ],
  },
  en: {
    button: 'Feedback',
    title: 'Feedback',
    desc: 'Tell us about bugs, confusing flows, or suggestions.',
    type: 'Type',
    content: 'Feedback',
    contact: 'Contact (optional)',
    contentPlaceholder: 'Describe what happened or what you want improved',
    contactPlaceholder: 'Email / phone / social handle, optional',
    page: 'Current page',
    submit: 'Submit',
    submitting: 'Submitting...',
    success: 'Thanks, feedback received.',
    error: 'Could not submit. Please try again later.',
    required: 'Please enter your feedback first.',
    close: 'Close feedback',
    types: [
      ['bug', 'Bug'],
      ['suggestion', 'Suggestion'],
      ['confused', 'Not sure how to use'],
      ['payment', 'Payment'],
    ],
  },
}

function currentPageUrl() {
  if (typeof window === 'undefined') return ''
  return `${window.location.pathname}${window.location.hash || ''}${window.location.search || ''}`
}

function FeedbackWidget() {
  const { language } = useLanguage()
  const t = copy[language] || copy.zh
  const [open, setOpen] = useState(false)
  const [type, setType] = useState('bug')
  const [content, setContent] = useState('')
  const [contact, setContact] = useState('')
  const [status, setStatus] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const pageUrl = currentPageUrl()

  useEffect(() => {
    const openFeedback = (event) => {
      const detail = event.detail || {}
      if (detail.type) setType(detail.type)
      if (detail.content) setContent(detail.content)
      setStatus('')
      setError('')
      setOpen(true)
    }
    window.addEventListener('app:open-feedback', openFeedback)
    return () => window.removeEventListener('app:open-feedback', openFeedback)
  }, [])

  async function handleSubmit(event) {
    event.preventDefault()
    setStatus('')
    setError('')
    if (!content.trim()) {
      setError(t.required)
      return
    }
    setSubmitting(true)
    try {
      await submitUserFeedback({
        type,
        content,
        contact,
        pageUrl,
        userAgent: navigator.userAgent || '',
      })
      setContent('')
      setContact('')
      setStatus(t.success)
    } catch {
      setError(t.error)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <button
        type="button"
        className="feedback-floating-button"
        onClick={() => setOpen(true)}
        aria-label={t.button}
      >
        <MessageSquareText size={17} />
        <span>{t.button}</span>
      </button>
      {open && (
        <div className="feedback-panel-backdrop" onClick={() => setOpen(false)}>
          <aside className="feedback-panel" role="dialog" aria-modal="true" aria-labelledby="feedback-title" onClick={(event) => event.stopPropagation()}>
            <header>
              <div>
                <h2 id="feedback-title">{t.title}</h2>
                <p>{t.desc}</p>
              </div>
              <button type="button" onClick={() => setOpen(false)} aria-label={t.close}>
                <X size={18} />
              </button>
            </header>
            <form onSubmit={handleSubmit}>
              <label>
                <span>{t.type}</span>
                <select value={type} onChange={(event) => setType(event.target.value)}>
                  {t.types.map(([value, label]) => (
                    <option value={value} key={value}>{label}</option>
                  ))}
                </select>
              </label>
              <label>
                <span>{t.content}</span>
                <textarea
                  value={content}
                  onChange={(event) => setContent(event.target.value)}
                  placeholder={t.contentPlaceholder}
                  rows={5}
                />
              </label>
              <label>
                <span>{t.contact}</span>
                <input
                  value={contact}
                  onChange={(event) => setContact(event.target.value)}
                  placeholder={t.contactPlaceholder}
                />
              </label>
              <p className="feedback-page-hint">{t.page}: {pageUrl || '-'}</p>
              {status && <p className="feedback-submit-state is-success" role="status">{status}</p>}
              {error && <p className="feedback-submit-state is-error" role="alert">{error}</p>}
              <button type="submit" className="primary-button" disabled={submitting}>
                {submitting ? t.submitting : t.submit}
                <Send size={16} />
              </button>
            </form>
          </aside>
        </div>
      )}
    </>
  )
}

export default FeedbackWidget
